import type { AuditEvent, AuditMetadata } from "@webyonetim/contracts";
import * as Dialog from "radix-ui/dialog";

import { Button } from "../../components/ui/Controls";
import { CodeValue } from "../../components/ui/ResponsiveData";
import { formatDateTime } from "../../lib/format";

export const REDACTED_VALUE = "•••••••• (gizlendi)" as const;

function cleanString(value: string): string {
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/gu, "")
    .replace(/\u001B\[[0-?]*[ -/]*[@-~]/gu, "");
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function redactValue(key: string, value: unknown): unknown {
  if (/secret|token|password|credential|recovery|bootstrap|cookie|authorization/iu.test(key))
    return REDACTED_VALUE;
  if (typeof value === "string") {
    const cleaned = cleanString(value);
    return cleaned === REDACTED_VALUE ? REDACTED_VALUE : cleaned;
  }
  if (Array.isArray(value)) return value.map((item) => redactValue("", item));
  if (isRecord(value)) return redactMetadata(value);
  if (value === null || typeof value === "number" || typeof value === "boolean") return value;
  return null;
}

function redactMetadata(
  metadata: AuditMetadata | Readonly<Record<string, unknown>> | undefined,
): Readonly<Record<string, unknown>> {
  if (metadata === undefined) return {};
  const redacted: Record<string, unknown> = {};
  for (const key of Object.keys(metadata)) {
    const value: unknown = metadata[key];
    redacted[key] = redactValue(key, value);
  }
  return redacted;
}

function MetadataBlock({
  metadata,
  title,
}: {
  readonly metadata: AuditMetadata | undefined;
  readonly title: string;
}) {
  const redacted = redactMetadata(metadata);
  return (
    <section>
      <h3>{title}</h3>
      {Object.keys(redacted).length === 0 ? (
        <p>Bilgi yok</p>
      ) : (
        <pre>{JSON.stringify(redacted, null, 2)}</pre>
      )}
    </section>
  );
}

export function AuditDetail({
  event,
  onOpenChange,
  open,
}: {
  readonly event: AuditEvent;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title>Denetim kaydı ayrıntısı</Dialog.Title>
          <Dialog.Description>
            Değiştirilemez olayın doğrulanmış ve gizli alanları redakte edilmiş ayrıntısı.
          </Dialog.Description>
          <dl className="stack">
            <div>
              <dt>Zaman</dt>
              <dd>
                <time dateTime={event.occurredAt}>{formatDateTime(event.occurredAt)}</time>
              </dd>
            </div>
            <div>
              <dt>Aktör</dt>
              <dd>
                {cleanString(event.actor.label)} · {event.actor.type} ·{" "}
                <CodeValue>{event.actor.id}</CodeValue>
              </dd>
            </div>
            <div>
              <dt>Kaynak</dt>
              <dd>
                {event.target === undefined ? (
                  "Kaynak yok"
                ) : (
                  <>
                    {event.target.type} ·{" "}
                    {event.target.label === undefined ? null : cleanString(event.target.label)} ·{" "}
                    <CodeValue>{event.target.id}</CodeValue>
                  </>
                )}
              </dd>
            </div>
            <div>
              <dt>Sonuç</dt>
              <dd>{event.outcome}</dd>
            </div>
            {event.sourceIp === undefined ? null : (
              <div>
                <dt>Kaynak IP</dt>
                <dd>
                  <CodeValue>{event.sourceIp}</CodeValue>
                </dd>
              </div>
            )}
            <div>
              <dt>İstek kimliği</dt>
              <dd>
                <CodeValue>{event.requestId}</CodeValue>
              </dd>
            </div>
            <div>
              <dt>Korelasyon kimliği</dt>
              <dd>
                <CodeValue>{event.correlationId}</CodeValue>
              </dd>
            </div>
            {event.problemCode === undefined ? null : (
              <div>
                <dt>Kararlı hata kodu</dt>
                <dd>
                  <CodeValue>{event.problemCode}</CodeValue>
                </dd>
              </div>
            )}
          </dl>
          <div className="two-column">
            <MetadataBlock metadata={event.before} title="Önce" />
            <MetadataBlock metadata={event.after} title="Sonra" />
          </div>
          <Dialog.Close asChild>
            <Button variant="secondary">Denetim ayrıntısını kapat</Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
